//////////////////////////////////////////////////
// Starter Template Projects
// Load default template projects from /starter-projects into DB
//////////////////////////////////////////////////

import OS from '../tablet/OS';
import IO from '../tablet/IO';

const TEMPLATES_KEY = 'starter_templates_loaded_v1';
const TEMPLATE_FILES = [
    'starter-projects/dance.txt',
    'starter-projects/animal-race.txt'
];

export default class StarterTemplates {
    /**
     * Load starter templates into database on first run
     * Templates are marked with gallery='templates' to distinguish from user projects
     */
    static loadTemplates(callback) {
        console.log('🎯 [StarterTemplates] Checking if templates need to be loaded...');

        // Check if templates already loaded (robust: AndroidInterface → localStorage → OS)
        const checkLoaded = (cb) => {
            try {
                if (typeof window !== 'undefined' && window.AndroidInterface && typeof window.AndroidInterface.io_getfile === 'function') {
                    const val = window.AndroidInterface.io_getfile(TEMPLATES_KEY);
                    cb(val);
                    return;
                }
            } catch (_) {}
            try {
                const key = 'scratchjr_file_' + TEMPLATES_KEY;
                const val = localStorage.getItem(key);
                cb(val || '');
                return;
            } catch (_) {}
            // Fallback to OS.getfile if above not available
            try {
                OS.getfile(TEMPLATES_KEY, cb);
                return;
            } catch (e) {
                cb('');
            }
        };

        checkLoaded((result) => {
            if (result && result !== 'NaN' && result !== '') {
                console.log('✅ [StarterTemplates] Templates already loaded, skipping');
                if (callback) callback();
                return;
            }

            console.log('📦 [StarterTemplates] Loading templates for first time...');
            let loadedCount = 0;
            let failedCount = 0;

            TEMPLATE_FILES.forEach((templateFile, index) => {
                IO.requestFromServer(templateFile, (responseText) => {
                    try {
                        // Parse the template data (it's a JSON array with one project object)
                        const templateData = JSON.parse(responseText);
                        if (!Array.isArray(templateData) || templateData.length === 0) {
                            throw new Error('Invalid template format');
                        }

                        const template = templateData[0];
                        const projectData = IO.parseProjectData(template);

                        // Mark as template by setting gallery='templates'
                        projectData.gallery = 'templates';
                        projectData.version = projectData.version || 'iOSv01';
                        projectData.deleted = 'NO';
                        projectData.mtime = Date.now().toString();
                        projectData.ctime = Date.now().toString();

                        // Ensure json and thumbnail are objects for insert
                        if (typeof projectData.json === 'string') {
                            projectData.json = JSON.parse(projectData.json);
                        }
                        if (typeof projectData.thumbnail === 'string') {
                            projectData.thumbnail = JSON.parse(projectData.thumbnail);
                        }

                        // Insert into database with explicit ID
                        StarterTemplates.insertTemplate(projectData, (success) => {
                            if (success) {
                                loadedCount++;
                                console.log(`✅ [StarterTemplates] Loaded template: ${projectData.name} (${loadedCount}/${TEMPLATE_FILES.length})`);
                            } else {
                                failedCount++;
                                console.error(`❌ [StarterTemplates] Failed to load template: ${projectData.name}`);
                            }

                            // Check if all templates processed
                            if (loadedCount + failedCount === TEMPLATE_FILES.length) {
                                StarterTemplates.finishLoading(loadedCount, failedCount, callback);
                            }
                        });
                    } catch (error) {
                        failedCount++;
                        console.error(`❌ [StarterTemplates] Error parsing template ${templateFile}:`, error);

                        if (loadedCount + failedCount === TEMPLATE_FILES.length) {
                            StarterTemplates.finishLoading(loadedCount, failedCount, callback);
                        }
                    }
                });
            });
        });
    }

    /**
     * Insert a single template into the database
     */
    static insertTemplate(template, callback) {
        const json = {};
        const keylist = ['id', 'name', 'version', 'deleted', 'mtime', 'ctime', 'gallery', 'isgift', 'json', 'thumbnail'];
        const values = '?,?,?,?,?,?,?,?,?,?';

        json.values = [
            template.id,
            template.name,
            template.version,
            template.deleted,
            template.mtime,
            template.ctime,
            template.gallery,
            template.isgift || '0',
            JSON.stringify(template.json),
            JSON.stringify(template.thumbnail)
        ];

        json.stmt = 'insert or replace into ' + OS.database + ' (' + keylist.toString() + ') values (' + values + ')';

        OS.stmt(json, (response) => {
            console.log('📋 [StarterTemplates] Insert response:', response);
            callback(true);
        });
    }

    /**
     * Mark templates as loaded and call completion callback
     */
    static finishLoading(loadedCount, failedCount, callback) {
        console.log(`🎉 [StarterTemplates] Template loading complete: ${loadedCount} loaded, ${failedCount} failed`);

        // Mark templates as loaded
        OS.setfile(TEMPLATES_KEY, btoa('loaded'), () => {
            console.log('✅ [StarterTemplates] Marked templates as loaded');
            if (callback) callback();
        });
    }

    /**
     * Query template projects from database
     */
    static getTemplates(callback) {
        const json = {};
        json.cond = 'deleted = ? AND gallery = ?';
        json.items = ['name', 'thumbnail', 'id', 'json'];
        json.values = ['NO', 'templates'];
        json.order = 'ctime asc';  // Show in order they were created

        IO.query(OS.database, json, callback);
    }

    /**
     * Create a user copy of a template project
     * This implements the "copy-on-open" pattern
     */
    static copyTemplate(templateId, callback) {
        console.log('📋 [StarterTemplates] Copying template:', templateId);

        // Load the template from database
        IO.getObject(templateId, (responseText) => {
            try {
                const templateArray = JSON.parse(responseText);
                if (!Array.isArray(templateArray) || templateArray.length === 0) {
                    throw new Error('Template not found');
                }

                const template = IO.parseProjectData(templateArray[0]);

                // Parse json and thumbnail if they're strings
                if (typeof template.json === 'string') {
                    template.json = JSON.parse(template.json);
                }
                if (typeof template.thumbnail === 'string') {
                    template.thumbnail = JSON.parse(template.thumbnail);
                }

                // Use uniqueProjectName to avoid duplicates (e.g., "Animal Race 1", "Animal Race 2")
                IO.uniqueProjectName(template, (uniqueTemplate) => {
                    // Create a new user project with the unique name
                    const newProject = {
                        name: uniqueTemplate.name,
                        version: template.version,
                        json: template.json,
                        thumbnail: template.thumbnail,
                        isgift: '0',
                        mtime: Date.now().toString()
                    };

                    console.log('📋 [StarterTemplates] Creating user copy:', newProject.name);

                    IO.createProject(newProject, (newProjectId) => {
                        console.log('✅ [StarterTemplates] Created user project:', newProjectId);
                        if (callback) callback(newProjectId);
                    });
                }, true); // useOne = true to ensure first copy is "Template Name 1"
            } catch (error) {
                console.error('❌ [StarterTemplates] Error copying template:', error);
                if (callback) callback(null);
            }
        });
    }
}
